import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import type { components, paths } from "./zz_schema";

import { useAuth } from "@/auth/auth";

type SecretType =
	paths["/v1/organizations/{organizationId}/secrets/{secretType}/{secretName}"]["delete"]["parameters"]["path"]["secretType"];

export type SecretRow = components["schemas"]["SecretMetadata"];

// Backends that predate the {secrets: [...]} envelope return a bare array;
// remove after the hardened backend is rolled out everywhere.
export function normalizeSecretList(body: unknown): SecretRow[] | undefined {
	if (body === undefined) return undefined;
	if (Array.isArray(body)) return body as SecretRow[];
	return (body as { secrets: SecretRow[] }).secrets;
}

export function useOrganizationSecrets(organizationId?: string) {
	const auth = useAuth();

	return useSWR(
		organizationId ? `/v1/organizations/${organizationId}/secrets` : null,
		() => {
			if (!organizationId) return undefined;
			return auth.client.GET("/v1/organizations/{organizationId}/secrets", {
				params: {
					path: {
						organizationId,
					},
					query: {
						// The server default is 100; the page has no pager yet, so ask
						// for the maximum. Must stay at or below the server's
						// maxSecretListLimit, which REJECTS larger values with a 400.
						// TODO(turkenh): paging UI when an org approaches 500 secrets.
						limit: 500,
					},
				},
			});
		},
	);
}

export function useDeleteOrganizationSecret(organizationId?: string) {
	const auth = useAuth();

	return useSWRMutation(
		organizationId ? `/v1/organizations/${organizationId}/secrets` : null,
		(_, { arg }: { arg: { secretType: string; secretName: string } }) => {
			if (!organizationId) return undefined;
			return auth.client.DELETE(
				"/v1/organizations/{organizationId}/secrets/{secretType}/{secretName}",
				{
					params: {
						path: {
							organizationId,
							// The path param is spec-enumerated; rows carry plain
							// strings and the server validates the type anyway.
							secretType: arg.secretType as SecretType,
							secretName: arg.secretName,
						},
					},
				},
			);
		},
	);
}
